import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Checkbox } from './ui/checkbox.jsx';
import {
  TrendingDown,
  TrendingUp,
  DollarSign,
  Cpu,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Play,
  Info,
  Brain,
  Zap,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
  Lightbulb,
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveBadgeProps } from './confidenceBadge';
import { ShieldCheck } from 'lucide-react';

function RightsizingRecommendations({ clusterId, recommendations, onApply, onRefresh, loading }) {
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [showTooltip, setShowTooltip] = useState(null);
  const [mlActions, setMlActions] = useState([]);
  const [unifiedRecommendations, setUnifiedRecommendations] = useState([]);
  const [processingAction, setProcessingAction] = useState(null);
  const [activeIntents, setActiveIntents] = useState([]);
  // Sprint 6 Step 5: per-workload eviction-status map, fetched from the
  // backend endpoint that rolls up agent-pushed eviction_class per pod.
  // Keyed by "Kind/namespace/name" — same shape the backend emits.
  const [evictionStatus, setEvictionStatus] = useState(null);

  // Fetch ML pending actions and active intents
  useEffect(() => {
    if (clusterId) {
      fetchMlActions();
      fetchActiveIntents();
      fetchEvictionStatus();
    }
  }, [clusterId]);

  // Merge P95 recommendations with ML actions
  useEffect(() => {
    mergeRecommendations();
  }, [recommendations, mlActions]);

  const fetchMlActions = async () => {
    try {
      const response = await axios.get(`${API}/ml/actions/pending-approval/${clusterId}`);
      setMlActions(response.data.actions || []);
    } catch (error) {
      console.error('Failed to fetch ML actions:', error);
    }
  };

  const fetchActiveIntents = async () => {
    try {
      const response = await axios.get(`${API}/ml/active-intents/${clusterId}`);
      setActiveIntents(response.data.intents || []);
    } catch (error) {
      console.log('Active intents not available:', error);
      setActiveIntents([]);
    }
  };

  // Sprint 6 Step 5: pull per-workload eviction classification so the
  // workload cards can render the "Held by PDB X" indicator. Endpoint
  // returns {} when no node-state snapshot exists yet — render nothing.
  const fetchEvictionStatus = async () => {
    try {
      const response = await axios.get(`${API}/v1/clusters/${clusterId}/workloads/eviction-status`);
      setEvictionStatus(response.data.workloads || {});
    } catch (error) {
      console.log('Eviction status not available:', error);
      setEvictionStatus(null);
    }
  };

  const mergeRecommendations = () => {
    const merged = [];
    const seen = new Set();

    // First, add the heuristic recommendations.
    recommendations.forEach(rec => {
      const key = `${rec.namespace}/${rec.workload_name}`;
      // Guard against a workload appearing twice in the incoming list. Without it
      // every duplicate was pushed, so one Deployment could render two cards with
      // contradictory numbers. `seen` was being populated but never consulted here.
      if (seen.has(key)) return;
      seen.add(key);

      // Check if there's a matching ML action
      const mlMatch = mlActions.find(
        ml => ml.namespace === rec.namespace && ml.workload_name === rec.workload_name
      );

      if (mlMatch) {
        // COMBINED: Both P95 and ML agree - use average of both confidences
        const p95Confidence = rec.confidence_score || 70;
        const mlConfidence = (mlMatch.confidence || 0.7) * 100;
        const combinedConfidence = Math.round((p95Confidence + mlConfidence) / 2);

        merged.push({
          ...rec,
          source: 'combined',
          mlAction: mlMatch,
          confidence_score: Math.max(p95Confidence, mlConfidence),
          combined_confidence: combinedConfidence,
        });
      } else {
        // No matching ML action. Keep the lane the backend actually reported —
        // hardcoding 'p95' here is what made an intent and a live analysis rec
        // present themselves identically as "P95 Heuristic".
        merged.push({
          ...rec,
          source: rec.source || 'p95',
        });
      }
    });

    // Add ML-only actions (not in P95)
    mlActions.forEach(ml => {
      const key = `${ml.namespace}/${ml.workload_name}`;
      if (!seen.has(key)) {
        merged.push({
          workload_name: ml.workload_name,
          namespace: ml.namespace,
          current_resources: ml.current_resources,
          recommended_resources: ml.recommended_resources,
          potential_monthly_savings: ml.potential_monthly_savings,
          confidence_score: (ml.confidence || 0.7) * 100,
          source: 'ml',
          mlAction: ml,
          action_type: ml.action_type || 'downscale',
          criticality: ml.confidence >= 0.9 ? 'low' : ml.confidence >= 0.7 ? 'medium' : 'high',
        });
      }
    });

    setUnifiedRecommendations(merged);
  };

  const handleApproveML = async (actionId) => {
    try {
      setProcessingAction(actionId);
      await axios.post(`${API}/ml/actions/${actionId}/approve`);
      toast.success('Action approved and queued for execution');
      fetchMlActions();
    } catch (error) {
      toast.error('Failed to approve action');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleRejectML = async (actionId) => {
    try {
      setProcessingAction(actionId);
      await axios.post(`${API}/ml/actions/${actionId}/reject`);
      toast.success('Action rejected');
      fetchMlActions();
    } catch (error) {
      toast.error('Failed to reject action');
    } finally {
      setProcessingAction(null);
    }
  };

  // HPA badges — three variants surface the HPA-aware optimization story:
  //   1. CPU/memory-utilization HPA → indigo "aligned to N%" with the alignment math
  //   2. external/custom-metric HPA → amber "conservative" (limited optimization path)
  //   3. anti-oscillation hold → red "Hold — HPA scaled up recently" (suppressed downscale)
  // We render a small array of chips so all relevant signals are visible at once.
  const getHpaBadges = (recommendation) => {
    if (!recommendation || !recommendation.hpa_managed) return null;
    const tt = (recommendation.hpa_target_type || '').toLowerCase();
    const targetVal = recommendation.hpa_target_value;
    const isCpu = tt.includes('cpu');
    const isMem = tt.includes('memory') || tt.includes('mem');
    const isExternal = !isCpu && !isMem;
    const badges = [];

    if (isCpu || isMem) {
      const axis = isCpu ? 'CPU' : 'Mem';
      const label = targetVal ? `HPA (${axis}) — aligned to ${Math.round(targetVal)}%` : `HPA (${axis})`;
      const tooltip = targetVal
        ? `${axis} request aligned so HPA sees ~${Math.round(targetVal)}% utilization. Formula: p95 / (target/100) × buffer. Keeps HPA's scaling signal stable while still capturing savings.`
        : `An HPA scales this workload on ${tt}. Recommendation accounts for HPA behavior.`;
      badges.push(
        <Badge key="hpa-aligned" variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300 text-xs" title={tooltip}>
          {label}
        </Badge>
      );
    } else if (isExternal) {
      const tooltip = `HPA targets a custom/external metric (${tt || 'unknown'}). We can't predict scaling, so optimization runs P95 with conservative caps: 30% per-cycle downscale, 15% if changed recently, drift block, and 20-min scale-awareness window.`;
      badges.push(
        <Badge key="hpa-external" variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs" title={tooltip}>
          HPA (External) — conservative
        </Badge>
      );
    }

    if (recommendation.anti_oscillation_fired) {
      badges.push(
        <Badge key="hpa-hold" variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs" title="HPA scaled up recently — holding request stable for 30 min so HPA's signal can settle before we make resource changes.">
          Hold — HPA scaled up recently
        </Badge>
      );
    }

    const guards = recommendation.external_hpa_guards || [];
    if (guards.length > 0) {
      const tooltip = `External-HPA safeguards fired: ${guards.join(', ')}`;
      badges.push(
        <Badge key="hpa-guards" variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs" title={tooltip}>
          {guards.length} guard{guards.length === 1 ? '' : 's'} fired
        </Badge>
      );
    }

    return badges.length > 0 ? badges : null;
  };

  // Backwards-compat shim — old call sites (single-badge spot in card header).
  // RESOLVED SCOPE badge. Two policy scopes exist — a cluster-wide default and
  // namespace/label policies — so a workload's number is only explainable once
  // you can see WHICH policy won and what tier it carries. Mirrors the
  // effective-policy column CAST and ScaleOps surface.
  const MEM_TIER_LABEL = {
    p99_peak: 'Conservative',
    p95_peak: 'Balanced',
    usage: 'Aggressive',
  };

  const getScopeBadge = (rec) => {
    const tier = MEM_TIER_LABEL[rec.effective_mem_peak_floor];
    if (!tier) return null;
    const scope = rec.effective_policy_scope || 'cluster-default';
    const via = scope === 'cluster-default'
      ? 'cluster default'
      : `${scope} policy "${rec.effective_policy_name || rec.effective_policy_id}"`;
    const cls = {
      Conservative: 'bg-red-50 text-red-700 border-red-200',
      Balanced: 'bg-blue-50 text-blue-700 border-blue-200',
      Aggressive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    }[tier];
    return (
      <Badge
        variant="outline"
        className={`${cls} text-xs`}
        title={`Memory tier ${tier} (${rec.effective_mem_peak_floor}) via ${via}, P${rec.effective_base_percentile}`}
      >
        {tier} · via {scope === 'cluster-default' ? 'cluster' : scope}
      </Badge>
    );
  };

  // The what-if tier preview was removed 2026-08-19. It was computed at the
  // recommender while the displayed number is produced later in the pipeline
  // (stability overlay, core finalize, container-split override), so the two
  // could not be reconciled — it rendered "Aggressive 1596Mi" beside a card
  // recommending 7.47 GB. The scope badge above still shows which tier applied.

  // Renders the first badge from getHpaBadges.
  const getHpaBadge = (recommendation) => {
    const badges = getHpaBadges(recommendation);
    return badges ? <>{badges}</> : null;
  };

  // Sprint 6 Step 5: eviction-blocker indicator on workload cards.
  // Map of workload_key → eviction-status doc populated by an effect
  // below (fetches /v1/clusters/{id}/workloads/eviction-status). Renders
  // an orange chip so operators see *why* a workload's recommendation
  // may stall (PDB X / sticky annotation / singleton-PVC).
  const getEvictionBlockerBadge = (recommendation) => {
    if (!recommendation || !evictionStatus) return null;
    const ns = recommendation.namespace;
    const name = recommendation.workload_name;
    const kind = recommendation.workload_kind || recommendation.kind || 'Deployment';
    if (!ns || !name) return null;
    const key = `${kind}/${ns}/${name}`;
    const status = evictionStatus[key];
    if (!status || !status.classification || status.classification === 'evictable') return null;
    const labelByClass = {
      pdb_blocks_all: status.blocking_pdb_name
        ? `Held by PDB ${status.blocking_pdb_namespace || ns}/${status.blocking_pdb_name}`
        : 'Held by PDB',
      pdb_partial: 'Restricted by PDB',
      singleton_pvc: 'Singleton + PVC (auto-sticky)',
      karpenter_do_not_disrupt: 'do-not-disrupt',
      ca_safe_to_evict_false: 'safe-to-evict=false',
      optops_sticky: 'optops.io/sticky',
      node_in_sticky_pool: 'on sticky pool',
    };
    const label = labelByClass[status.classification] || status.classification;
    const colorByClass = {
      pdb_blocks_all: 'bg-orange-50 text-orange-700 border-orange-300',
      pdb_partial: 'bg-amber-50 text-amber-700 border-amber-300',
      singleton_pvc: 'bg-purple-50 text-purple-700 border-purple-300',
    };
    const cls = colorByClass[status.classification] || 'bg-amber-50 text-amber-700 border-amber-300';
    return (
      <Badge key="eviction-blocker" variant="outline" className={`${cls} text-xs`} title={status.reason || label}>
        {label}
      </Badge>
    );
  };

  // K8s owner-kind badge — Deployment / StatefulSet / DaemonSet / Job / CronJob.
  // Color-coded so users can scan a list and instantly see what they're looking at;
  // StatefulSet is purple to match the policy-opt-in surface elsewhere in the UI.
  const getKindBadge = (kind) => {
    if (!kind) return null;
    const config = {
      Deployment: 'bg-blue-50 text-blue-700 border-blue-200',
      StatefulSet: 'bg-purple-50 text-purple-700 border-purple-200',
      DaemonSet: 'bg-amber-50 text-amber-700 border-amber-200',
      Job: 'bg-slate-50 text-slate-700 border-slate-200',
      CronJob: 'bg-teal-50 text-teal-700 border-teal-200',
      ReplicaSet: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    const cls = config[kind] || 'bg-gray-50 text-gray-700 border-gray-200';
    return (
      <Badge variant="outline" className={`${cls} text-xs`}>
        {kind}
      </Badge>
    );
  };

  // IOPS brake badge — the rightsizer refused to cut CPU because the workload
  // is I/O-bound (its low CPU is iowait, not spare capacity). Explains why a
  // seemingly idle workload shows no downsize. Backend sets `io_floor_applied`
  // on the recommendation when the brake actually lifted the rec to current.
  const getIoBoundBadge = (recommendation) => {
    if (!recommendation || !recommendation.io_floor_applied) return null;
    return (
      <Badge
        key="io-bound"
        variant="outline"
        className="bg-cyan-50 text-cyan-700 border-cyan-300 text-xs flex items-center gap-1"
        title="I/O-bound: CPU downsize blocked. Low CPU here is iowait (disk-bound), not spare capacity — cutting it would throttle the I/O path."
      >
        <HardDrive className="w-3 h-3" /> IO-bound
      </Badge>
    );
  };

  // Source badge for recommendation origin
  const getSourceBadge = (source, actionType) => {
    const badges = {
      'p95': { color: 'bg-blue-100 text-blue-800', icon: Zap, text: 'P95 Heuristic' },
      'ml': { color: 'bg-purple-100 text-purple-800', icon: Brain, text: 'ML Prediction' },
      'combined': { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'P95 + ML Combined' },
      // Real lanes that previously fell through to the 'p95' default and were
      // therefore all labelled "P95 Heuristic" — including cards that came from a
      // stale intent or the spike watcher. A wrong provenance label is worse than an
      // ugly one: it tells the operator two different calculators are the same thing.
      'intent': { color: 'bg-amber-100 text-amber-800', icon: Zap, text: 'Intent (pending)' },
      'live_analysis': { color: 'bg-blue-100 text-blue-800', icon: Zap, text: 'Live Analysis' },
      'spike_watcher': { color: 'bg-orange-100 text-orange-800', icon: ArrowUp, text: 'Spike Watcher' },
    };
    const config = badges[source] || { color: 'bg-slate-100 text-slate-700', icon: Zap, text: source || 'Unknown source' };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const getActionTypeBadge = (actionType) => {
    if (actionType === 'upscale') {
      return <Badge className="bg-orange-100 text-orange-800 flex items-center gap-1"><ArrowUp className="w-3 h-3" /> Upscale</Badge>;
    } else if (actionType === 'downscale') {
      return <Badge className="bg-green-100 text-green-800 flex items-center gap-1"><ArrowDown className="w-3 h-3" /> Downscale</Badge>;
    }
    return null;
  };

  // Simple tooltip component
  const Tooltip = ({ children, content, id }) => (
    <div className="relative inline-block">
      {children}
      {showTooltip === id && (
        <div className="absolute z-10 w-64 p-2 mt-1 text-xs text-white bg-gray-800 rounded-md shadow-lg -left-32">
          {content}
          <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 -top-1 left-1/2 -translate-x-1/2"></div>
        </div>
      )}
    </div>
  );
  const [applying, setApplying] = useState(false);

  const handleSelectRecommendation = (recommendation, checked) => {
    if (checked) {
      setSelectedRecommendations([...selectedRecommendations, recommendation]);
    } else {
      setSelectedRecommendations(selectedRecommendations.filter(r => r.workload_name !== recommendation.workload_name));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecommendations([...recommendations]);
    } else {
      setSelectedRecommendations([]);
    }
  };

  const handleApplySelected = async () => {
    if (selectedRecommendations.length === 0) {
      toast.error('Please select at least one recommendation');
      return;
    }

    setApplying(true);
    try {
      await onApply(selectedRecommendations);
      setSelectedRecommendations([]);
    } finally {
      setApplying(false);
    }
  };

  const getCriticalityBadge = (criticality) => {
    const config = {
      'low': { color: 'bg-green-100 text-green-800', text: 'Low Risk' },
      'medium': { color: 'bg-yellow-100 text-yellow-800', text: 'Medium Risk' },
      'high': { color: 'bg-red-100 text-red-800', text: 'High Risk' }
    };

    const badgeConfig = config[criticality] || config['medium'];
    return (
      <Badge className={badgeConfig.color}>
        {badgeConfig.text}
      </Badge>
    );
  };

  // State-aware badge — non-ok states render distinct visuals so users immediately
  // see when a rec rests on a rebuilding profile, missing recent data, or a
  // workload that's still in its first hour. Logic lives in resolveBadgeProps
  // (module-scope, exported for unit tests); this just renders the result.
  const getConfidenceBadge = (confidencePercent, recommendation) => {
    const props = resolveBadgeProps(recommendation, confidencePercent);
    return (
      <Badge className={props.color} title={props.tooltip}>
        {props.label}
      </Badge>
    );
  };

  // Utility functions for formatting resources (handles both K8s strings and numbers)
  const formatCPU = (cpuValue) => {
    if (!cpuValue && cpuValue !== 0) return 'N/A';
    // Already a K8s string like '600m' or '1.5'
    if (typeof cpuValue === 'string') return cpuValue;
    // Numeric value - detect if it's millicores or cores
    if (typeof cpuValue === 'number') {
      // Values > 10 are likely millicores (from DB), convert to cores
      // Typical CPU requests are 10m-4000m (0.01-4 cores)
      // If someone sets 10+ cores, that's unusual but we handle it
      let coresValue = cpuValue;
      if (cpuValue > 10) {
        // This is millicores, convert to cores
        coresValue = cpuValue / 1000;
      }

      if (coresValue >= 1) {
        return `${coresValue.toFixed(2)} cores`;
      } else {
        return `${Math.round(coresValue * 1000)}m`;
      }
    }
    return String(cpuValue);
  };

  const formatMemory = (memoryBytes) => {
    if (!memoryBytes && memoryBytes !== 0) return 'N/A';
    // Already a K8s string like '1.2Gi' or '512Mi'
    if (typeof memoryBytes === 'string') return memoryBytes;
    // Numeric value (bytes)
    if (typeof memoryBytes === 'number') {
      const gb = memoryBytes / (1024 * 1024 * 1024);
      const mb = memoryBytes / (1024 * 1024);
      if (gb >= 1) {
        return `${gb.toFixed(2)} GB`;
      } else {
        return `${Math.round(mb)} MB`;
      }
    }
    return String(memoryBytes);
  };

  // Parse K8s resource string to numeric value
  const parseCPU = (value) => {
    if (typeof value === 'number') {
      // Values > 10 are likely millicores from DB, normalize to cores
      // This ensures consistent units for percentage calculations
      return value > 10 ? value / 1000 : value;
    }
    if (typeof value === 'string') {
      if (value.endsWith('m')) return parseInt(value) / 1000;
      return parseFloat(value) || 0;
    }
    return 0;
  };

  const parseMemory = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (value.endsWith('Gi')) return parseFloat(value) * 1024 * 1024 * 1024;
      if (value.endsWith('Mi')) return parseFloat(value) * 1024 * 1024;
      if (value.endsWith('Ki')) return parseFloat(value) * 1024;
      return parseFloat(value) || 0;
    }
    return 0;
  };

  const formatResourceChange = (current, recommended, resourceType = 'cpu') => {
    // Parse to numbers for calculation
    const parseFunc = resourceType === 'cpu' ? parseCPU : parseMemory;
    const currentNum = parseFunc(current);
    const recommendedNum = parseFunc(recommended);

    const change = recommendedNum - currentNum;
    const percentage = currentNum > 0 ? ((change / currentNum) * 100) : 0;
    const isIncrease = change > 0;

    const formatValue = resourceType === 'cpu' ? formatCPU : formatMemory;

    return (
      <div className={`flex items-center space-x-1 ${isIncrease ? 'text-red-600' : 'text-green-600'}`}>
        {isIncrease ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        <span className="text-sm">
          {formatValue(current)} → {formatValue(recommended)} ({isIncrease ? '+' : ''}{percentage.toFixed(1)}%)
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="skeleton h-32 rounded"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Optimizations Panel - Shows intents/stepwise optimizations in progress */}
      {activeIntents.length > 0 && (
        <Card className="border-2 border-blue-500 bg-blue-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Zap className="w-5 h-5 animate-pulse" />
              Active Optimizations ({activeIntents.length} in progress)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeIntents.map((intent, idx) => {
                const workloadId = intent.workload_id || `${intent.namespace}/${intent.workload_name}`;
                const namespace = workloadId.split('/')[0] || 'default';
                const name = workloadId.split('/').pop() || workloadId;

                return (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <div>
                        <div className="font-medium text-gray-800">{name}</div>
                        <div className="text-xs text-gray-500">{namespace}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className="bg-blue-100 text-blue-800">
                        {intent.hysteresis_state || intent.status || 'optimizing'}
                      </Badge>
                      {intent.convergence_steps_remaining !== undefined && (
                        <span className="text-xs text-gray-500">
                          {intent.convergence_steps_remaining} steps remaining
                        </span>
                      )}
                      {intent.confidence && (
                        <span className="text-xs text-green-600">
                          {Math.round(intent.confidence * 100)}% confidence
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              These workloads are being optimized automatically. The system applies changes in small steps to ensure zero-downtime.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Checkbox
            checked={selectedRecommendations.length === unifiedRecommendations.length && unifiedRecommendations.length > 0}
            onCheckedChange={handleSelectAll}
            className="mr-2"
          />
          <span className="text-sm text-gray-600">
            {selectedRecommendations.length} of {unifiedRecommendations.length} selected
          </span>
          <span className="text-xs text-gray-500">
            ({mlActions.length} ML, {recommendations.length} P95)
          </span>
        </div>

        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>

          <Button
            onClick={handleApplySelected}
            disabled={selectedRecommendations.length === 0 || applying}
          >
            <Play className="w-4 h-4 mr-2" />
            {applying ? 'Applying...' : `Apply Selected (${selectedRecommendations.length})`}
          </Button>
        </div>
      </div>

      {/* Recommendations List */}
      {unifiedRecommendations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Recommendations Available</h3>
            <p className="text-gray-500 text-center">
              Your workloads are already optimally sized, or there's insufficient data for analysis.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {unifiedRecommendations.map((recommendation, index) => (
            <Card key={index} className={`hover:shadow-md transition-shadow ${recommendation.source === 'combined' ? 'ring-2 ring-green-200' : ''}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {/* Apply gate — rec always renders; checkbox disabled until
                        confidence clears apply_gate_threshold. Defaults to true
                        when missing (legacy recs from external code paths). */}
                    <Checkbox
                      checked={selectedRecommendations.some(r => r.workload_name === recommendation.workload_name)}
                      onCheckedChange={(checked) => handleSelectRecommendation(recommendation, checked)}
                      disabled={recommendation.apply_ready === false}
                      title={recommendation.apply_ready === false
                        ? `Building confidence — needs ≥${recommendation.apply_gate_threshold || 80}% to apply (currently ${Math.round((recommendation.confidence || 0) * 100)}%)`
                        : ''}
                    />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-lg">{recommendation.workload_name}</CardTitle>
                        {getKindBadge(recommendation.workload_kind)}
                        {getScopeBadge(recommendation)}
                        {getHpaBadge(recommendation)}
                        {getEvictionBlockerBadge(recommendation)}
                        {getIoBoundBadge(recommendation)}
                        {recommendation.apply_ready === false && (() => {
                          // Show live progress: current % climbing toward the apply gate.
                          // Trend arrow compares to last cycle's confidence (from
                          // the decision log) so users can SEE it building.
                          const cur = Math.round((recommendation.confidence ?? 0) * 100);
                          const thr = recommendation.apply_gate_threshold || 80;
                          // Self-defense against backend lag: apply_ready can stay false
                          // for a cycle after the numeric gate is cleared (backend has
                          // additional criteria — observation window, sample count).
                          // Don't render "Building · 100%" once the explicit threshold
                          // is met; the badge will reappear if confidence drops back.
                          if (cur >= thr) return null;
                          const pct = Math.min(100, Math.round((cur / thr) * 100));
                          const prev = typeof recommendation.previous_confidence === 'number'
                            ? Math.round(recommendation.previous_confidence * 100) : null;
                          let arrow = '', delta = '';
                          if (prev !== null) {
                            const diff = cur - prev;
                            if (diff > 0) { arrow = '↑'; delta = `+${diff}% since last cycle`; }
                            else if (diff < 0) { arrow = '↓'; delta = `${diff}% since last cycle`; }
                            else { delta = 'unchanged since last cycle'; }
                          }
                          // "Building" implies MORE DATA is needed — misleading when the
                          // profile is already full (coverage+maturity high) and the gap
                          // is purely the variance penalty settling after pod churn.
                          // Live-caught: optops-backend at coverage 99% / maturity Full
                          // showed "Building 65%" while the only discount was cv=29%.
                          const comps = recommendation.confidence_components;
                          const dataFull = comps && (comps.coverage ?? 0) >= 0.8 && (comps.maturity ?? 0) >= 0.95;
                          const word = dataFull ? 'Settling' : 'Building';
                          const why = dataFull
                            ? 'Profile is fully learned — the recommendation is settling after recent changes; the percentage recovers as consecutive cycles agree.'
                            : 'Profile is still building — the percentage rises as more data accumulates.';
                          return (
                            <div className="flex items-center gap-2"
                                 title={`Apply gate: needs ≥${thr}% confidence; currently ${cur}%. ${prev !== null ? delta + '. ' : ''}${why}`}>
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs whitespace-nowrap">
                                {`${word} · ${cur}% / ${thr}%${arrow ? ` ${arrow}` : ''}`}
                              </Badge>
                              <div className="w-20 h-1.5 bg-amber-100 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-500 transition-all duration-500"
                                     style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      <p className="text-sm text-gray-500">{recommendation.namespace}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {getSourceBadge(recommendation.source)}
                    {getActionTypeBadge(recommendation.action_type)}
                    {getCriticalityBadge(recommendation.criticality)}
                    {recommendation.slo_baseline_ready && (
                      <Badge
                        title={
                          'Golden-signal baseline captured for this workload — ' +
                          'rightsizing apply will be observed against latency, error rate, ' +
                          'and throughput at +5min and +15min.'
                        }
                        className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1 text-xs"
                      >
                        <ShieldCheck className="w-3 h-3" />
                        SLO baseline ready
                      </Badge>
                    )}
                    {getConfidenceBadge(
                      (() => {
                        // Normalize: backend writes confidence_score on a 0–1 scale; legacy
                        // ML/p95 paths sometimes send 0–100 directly. Treat ≤1 as fractional.
                        const raw =
                          recommendation.combined_confidence ??
                          (typeof recommendation.confidence_score === 'number'
                            ? recommendation.confidence_score
                            : (recommendation.confidence ?? 0));
                        return Math.round(raw <= 1 ? raw * 100 : raw);
                      })(),
                      recommendation
                    )}
                    {recommendation.apply_strategy && (
                      <Badge className={
                        recommendation.apply_strategy === 'ssa'
                          ? 'bg-purple-100 text-purple-800'
                          : recommendation.apply_strategy === 'webhook'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }>
                        {recommendation.apply_strategy}
                      </Badge>
                    )}
                    {recommendation.base_percentile_used && (
                      <Badge variant="outline" className="text-xs">
                        P{recommendation.base_percentile_used}
                      </Badge>
                    )}
                    {recommendation.annotations_written && (
                      <Badge className="bg-green-100 text-green-800 text-xs">annotated</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Resource Changes */}
                  {(recommendation.action !== 'bin_pack' && recommendation.current_resources && recommendation.recommended_resources) && (
                    <>
                      <div className="space-y-3">
                        <h4 className="font-medium flex items-center">
                          <Cpu className="w-4 h-4 mr-2" />
                          CPU Changes
                        </h4>
                        <div className="space-y-1">
                          <div className="text-sm text-gray-600">Request:</div>
                          {formatResourceChange(
                            recommendation.current_resources?.cpu_request ?? 0,
                            recommendation.recommended_resources?.cpu_request ?? 0,
                            'cpu'
                          )}
                          <div className="text-sm text-gray-600 mt-2">Limit:</div>
                          {formatResourceChange(
                            recommendation.current_resources?.cpu_limit ?? 0,
                            recommendation.recommended_resources?.cpu_limit ?? 0,
                            'cpu'
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-medium flex items-center">
                          <HardDrive className="w-4 h-4 mr-2" />
                          Memory Changes
                        </h4>
                        <div className="space-y-1">
                          <div className="text-sm text-gray-600">Request:</div>
                          {formatResourceChange(
                            recommendation.current_resources?.memory_request ?? 0,
                            recommendation.recommended_resources?.memory_request ?? 0,
                            'memory'
                          )}
                          <div className="text-sm text-gray-600 mt-2">Limit:</div>
                          {formatResourceChange(
                            recommendation.current_resources?.memory_limit ?? 0,
                            recommendation.recommended_resources?.memory_limit ?? 0,
                            'memory'
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Ephemeral Storage Changes (if available) */}
                  {(recommendation.recommended_resources?.ephemeral_storage_request > 0) && (
                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center">
                        <HardDrive className="w-4 h-4 mr-2 text-slate-500" />
                        Ephemeral Storage
                      </h4>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-600">Request:</div>
                        {formatResourceChange(
                          recommendation.current_resources?.ephemeral_storage_request ?? 0,
                          recommendation.recommended_resources?.ephemeral_storage_request ?? 0,
                          'memory'
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          (Includes 20% buffer over peak usage)
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Savings & Impact */}
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Impact
                      <Tooltip
                        id={`cost-${recommendation.workload_name}`}
                        content="Cost calculation based on: CPU cost ($0.0416/vCPU/month), Memory cost ($0.0056/GB/month), and resource request differences between current and recommended values."
                      >
                        <Info
                          className="w-3 h-3 ml-1 text-gray-400 cursor-help"
                          onMouseEnter={() => setShowTooltip(`cost-${recommendation.workload_name}`)}
                          onMouseLeave={() => setShowTooltip(null)}
                        />
                      </Tooltip>
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Monthly Savings:</span>
                        <span className="text-sm font-medium text-green-600">
                          ${recommendation.potential_monthly_savings?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                      {/* Estimated environmental impact — hidden when no resources saved */}
                      {(recommendation.environmental_savings?.power_kwh_per_month > 0 ||
                        recommendation.environmental_savings?.co2_kg_per_month > 0) && (
                        <>
                          <div
                            className="flex justify-between"
                            title={`Estimated avoided power use per month — ~10W per vCPU × PUE 1.5 over 730h. Rough estimate.`}
                          >
                            <span className="text-sm text-gray-600">Power saved (est):</span>
                            <span className="text-sm font-medium text-emerald-600">
                              {recommendation.environmental_savings.power_kwh_per_month?.toFixed(1)} kWh/mo
                            </span>
                          </div>
                          <div
                            className="flex justify-between"
                            title={`Estimated CO₂ avoided per month at ${recommendation.environmental_savings.grid_intensity_gco2_per_kwh ?? 400} gCO₂/kWh grid intensity. Rough estimate.`}
                          >
                            <span className="text-sm text-gray-600">CO₂ avoided (est):</span>
                            <span className="text-sm font-medium text-emerald-600">
                              {recommendation.environmental_savings.co2_kg_per_month?.toFixed(1)} kg/mo
                            </span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Risk Level:</span>
                        <span className="text-sm">{recommendation.criticality ?? 'medium'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pod-wise Optimizations */}
                {recommendation.pod_optimizations && recommendation.pod_optimizations.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Pod-wise Optimizations ({recommendation.pod_optimizations.length} pods):</h5>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {recommendation.pod_optimizations.slice(0, 5).map((pod, idx) => (
                        <div key={idx} className="text-xs bg-slate-50 p-2 rounded flex justify-between">
                          <span className="font-mono text-slate-700">{pod.pod_name}</span>
                          <span className="text-slate-500">
                            {formatCPU(pod.current_cpu)} → {formatCPU(pod.recommended_cpu)} |
                            {formatMemory(pod.current_memory)} → {formatMemory(pod.recommended_memory)}
                          </span>
                        </div>
                      ))}
                      {recommendation.pod_optimizations.length > 5 && (
                        <div className="text-xs text-slate-500 text-center py-1">
                          +{recommendation.pod_optimizations.length - 5} more pods
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Container-Level Resources — only meaningful for multi-container
                    pods (sidecars, ambassadors). For single-container pods the
                    top-level CPU/Memory display is the same data, so we hide
                    this section to avoid the redundant "c / CPU 10m / Memory 64Mi"
                    panel (literal "c" being the container name from the manifest). */}
                {recommendation.recommended_resources?.container_resources &&
                  Object.keys(recommendation.recommended_resources.container_resources).length > 1 && (
                    <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
                      <h5 className="text-sm font-medium text-purple-800 mb-2 flex items-center">
                        <Cpu className="w-4 h-4 mr-1" />
                        Per-Container Resources ({Object.keys(recommendation.recommended_resources.container_resources).length} containers)
                      </h5>
                      <div className="space-y-2">
                        {Object.entries(recommendation.recommended_resources.container_resources).map(([containerName, resources]) => (
                          <div key={containerName} className="flex justify-between items-center text-xs bg-white p-2 rounded border border-purple-100">
                            <span className="font-mono text-purple-700 font-medium">{containerName}</span>
                            <div className="flex items-center space-x-3">
                              <span className="text-gray-600">
                                <span className="text-purple-600">CPU:</span> {resources.cpu || 'N/A'}
                              </span>
                              <span className="text-gray-600">
                                <span className="text-purple-600">Memory:</span> {resources.memory || 'N/A'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-purple-600 mt-2">
                        Each container is sized based on its individual usage pattern.
                      </p>
                    </div>
                  )}

                {/* Bin Packing Info */}
                {recommendation.action === 'bin_pack' && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <h5 className="text-sm font-medium text-blue-800 mb-1">Bin Packing Optimization</h5>
                    <p className="text-sm text-blue-700">
                      Consolidate {recommendation.pods_to_move?.length || 0} pods from {recommendation.source_node} to {recommendation.target_node}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Current utilization: Source {recommendation.current_utilization?.source?.toFixed(1)}%, Target {recommendation.current_utilization?.target?.toFixed(1)}%
                    </p>
                  </div>
                )}

                {/* Suggested policy template — only shown when this workload has no
                    custom policy matched yet (policy_id falsy or "default"). One-click
                    deep link into the Policies page with the template name pre-selected. */}
                {recommendation.suggested_template && (!recommendation.policy_id || recommendation.policy_id === 'default') && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-3">
                    <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h5 className="text-sm font-medium text-amber-900">Suggested policy:</h5>
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                          {recommendation.suggested_template.title}
                        </Badge>
                      </div>
                      <p className="text-xs text-amber-700 mt-1">{recommendation.suggested_template.description}</p>
                      <a
                        href={`/rightsizing?tab=policies&template=${encodeURIComponent(recommendation.suggested_template.id)}&namespace=${encodeURIComponent(recommendation.namespace || '')}&workload=${encodeURIComponent(recommendation.workload_name || '')}`}
                        className="text-xs text-amber-800 underline hover:text-amber-900 mt-1 inline-block"
                      >
                        Apply this template →
                      </a>
                    </div>
                  </div>
                )}

                {/* Reasoning */}
                {recommendation.reasoning && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <h5 className="text-sm font-medium text-gray-700 mb-1">Recommendation Reasoning:</h5>
                    <p className="text-sm text-gray-600">{recommendation.reasoning}</p>
                  </div>
                )}

                {/* Safety Warnings */}
                {recommendation.criticality === 'high' && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start">
                      <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 mr-2" />
                      <div>
                        <h5 className="text-sm font-medium text-red-800">High Risk Recommendation</h5>
                        <p className="text-sm text-red-700 mt-1">
                          This recommendation involves significant resource changes. Monitor closely after application.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default RightsizingRecommendations;